/**
 * VENDOR MODULES
 */
import React from 'react';

import { cn } from 'lib/utils';
import { withBasePath } from 'shared/utils/runtimeBasePath';
import { isEmbeddedInPlatformShell } from 'mfe/embedding';

interface AppLoaderProps {
  children?: React.ReactNode;
}

function AppLoader({ children }: AppLoaderProps) {
  const [isLoading, setIsLoading] = React.useState(true);
  const embedded = isEmbeddedInPlatformShell();

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    /* App bounding box: overlays (loader, no-events) use position:absolute inside here, not viewport fixed.
     * When embedded: content must be in-flow so the host gets height and the footer stays below (no overlap). */
    <div className={cn('relative w-full', embedded ? 'min-h-0 flex flex-col' : 'h-screen')}>
      <div
        className={cn(
          'absolute inset-0 z-[9999] bg-card flex flex-col items-center justify-center transition-opacity duration-500',
          isLoading ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="relative">
            <img
              src={withBasePath('/images/logo.png')}
              alt="Caribe7"
              className="w-64 md:w-80 lg:w-96 object-contain animate-pulse"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const next = target.nextElementSibling as HTMLElement;
                if (next) next.style.display = 'block';
              }}
            />
            <div className="hidden text-2xl font-bold text-center" style={{ display: 'none' }}>
              <div className="text-green-500">caribe</div>
              <div className="text-orange-500">7</div>
            </div>
          </div>

          <div className="text-muted-foreground text-lg font-medium">Loading...</div>

          <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin"></div>
        </div>
      </div>

      {/* When embedded: in-flow so host height = content height and footer is below. When standalone: absolute fill. */}
      <div
        className={cn(
          'flex flex-col transition-opacity duration-500',
          embedded
            ? 'min-h-0 flex flex-col'
            : 'absolute inset-0',
          isLoading ? 'opacity-0' : 'opacity-100'
        )}
      >
        {children}
      </div>
    </div>
  );
}

export default AppLoader;
